import { ITag } from '@root/query/tags'
import React from 'react'
import styled from 'styled-components'

import { Button, TableList, TextTooltip } from '@root/components'
import { TagPill } from '@root/components/TagPill/TagPill'
import { FaPen, FaTrash } from 'react-icons/fa6'
import { useScreen } from '@root/utils'
import { useTranslation } from 'react-i18next'

const ITEM_HEIGHT = 64

interface Props {
  tags: ITag[]
  onScrollBottom: () => void
  onEditTag: (tag: ITag) => void
  onDeleteTag: (tag: ITag) => void
}

const TagsList: React.FC<Props> = ({ tags, onScrollBottom, onEditTag, onDeleteTag }) => {
  const [t] = useTranslation()

  const { isMobile } = useScreen()

  return (
    <TableList<ITag>
      items={tags}
      itemHeight={ITEM_HEIGHT}
      onScrollBottom={onScrollBottom}
      renderItem={tag => (
        <>
          <StyledInfoContainer $isMobile={isMobile}>
            <StyledTagContainer $isMobile={isMobile}>
              <TagPill tag={tag} />
            </StyledTagContainer>

            <StyledText>{tag.description}</StyledText>
          </StyledInfoContainer>

          <StyledActionContainer>
            <TextTooltip tooltip={t('settings.tags.list.editTooltip')} placement="bottom-end">
              <Button
                icon={<FaPen />}
                size="sm"
                onClick={() => {
                  onEditTag(tag)
                }}
              ></Button>
            </TextTooltip>

            <TextTooltip tooltip={t('settings.tags.list.deleteTooltip')} placement="bottom-end">
              <Button
                icon={<FaTrash />}
                size="sm"
                intent="danger"
                onClick={() => onDeleteTag(tag)}
              ></Button>
            </TextTooltip>
          </StyledActionContainer>
        </>
      )}
    ></TableList>
  )
}

export default TagsList

const StyledInfoContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  width: 70%;

  gap: ${({ $isMobile, theme }) => ($isMobile ? theme.spacing.sm : theme.spacing.md)}rem;
`

const StyledActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const StyledTagContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  min-width: ${({ $isMobile }) => ($isMobile ? 100 : 175)}px;
  width: ${({ $isMobile }) => ($isMobile ? 100 : 175)}px;
  flex-shrink: 0;
`
